import React, { useEffect, useState, Suspense } from 'react'
import ReactDOM from 'react-dom'
import '@fs/react-scripts/polyfills'
import { useTranslation } from 'react-i18next'
import * as Sentry from '@sentry/react'
import { setAppLocales, essentialLocales, i18n } from '@fs/zion-locale'
import Root, { FrontierBaseProviders } from '@fs/zion-root'
import { Router } from '@fs/zion-router'
import { HelperMode } from '@fs/zion-helper-mode'
import { AppLayout, colors } from '@fs/zion-ui'
import { appPath, sentryDSN, mergeSentryConfig } from '@fs/zion-config'
import { getPreference, setPreference } from '@fs/zion-preferences'
import App from './App'
import * as serviceWorker from './serviceWorker'
import './locales'
import utils from './lib/utils'

const devEnv = process.env.NODE_ENV === 'development'

// Enable local feature flag development using the file "dev.flags.js"
// If that file exports anything truthy it will initialize feature flags in localhost mode
// and use the flags defined in that file, instead of flags defined in split.
const mockFlags = devEnv ? import('./dev.flags').then((m) => m.default) : undefined

const mockActivities = devEnv ? import('./dev.activities').then((m) => m.default) : undefined

/* Sentry initialization.
 * Sentry is disabled locally by default (SENTRY_DSN is unset and SENTRY_DISABLED=true in .env).
 * Snow runs locally via setupProxy.js and populates window.SERVER_DATA on every page load, so to enable Sentry in development, run:
 * SENTRY_DSN=<your-dsn> SENTRY_DISABLED=false npm start
 * Events will log under the "local" environment (TARGET_ENV in .env).
 * Additional debugging is gated conditionally by devEnv.
 */
if (sentryDSN) {
  /* Sample rates and strict filtering are hardcoded because CRA bakes REACT_APP_* env vars at BUILD time via webpack DefinePlugin — they cannot be changed at runtime via server config. To adjust these values, change them here and redeploy. */
  const sentryConfig = mergeSentryConfig({
    attachStacktrace: devEnv,
    debug: devEnv, // Log Sentry SDK diagnostics to the console in development.
    sampleRate: devEnv ? 1 : 0.01, // 0.0–1.0 fraction. 100% of events locally.
    strictFiltering: true, // Enhanced filtering via @fs/zion-config (https://github.com/fs-webdev/zion/pull/13771)
  })

  // eslint-disable-next-line no-console -- dev-locked debugging statement of value to the *browser* console.
  devEnv && console.log('Sentry config:', sentryConfig)

  Sentry.init(sentryConfig)
}

// Here we pass the mounted app path from the server to the client.
const base = appPath ? new URL(appPath).pathname : ''

const EmptyLoading = () => <div />

// Add any app logic here as flags, languages and translations are now configured and available
const FrontierWrapper = () => {
  setAppLocales(essentialLocales)

  const [t] = useTranslation()

  const [showFooter, setShowFooter] = useState({})
  const [isSourceBox, setIsSourceBox] = useState(false)
  const [open, setOpen] = useState(false)
  const footerProp = !showFooter ? { footer: { hidden: true } } : {}
  const fullScreenProp = !showFooter ? { fullscreen: true } : {}

  useEffect(() => {
    getPreference('sourcebox.alert-banner.dismissed')
      .then((pref) => {
        setOpen(isSourceBox && !pref)
        return undefined
      })
      .catch(console.error)
  }, [isSourceBox])

  const alertBanner =
    i18n.language === 'en'
      ? {
          type: 'info',
          message: t('source-box.learn-more-info'),
          confirmText: t('source-box.learn-more'),
          dismissText: t('source-box.dismiss'),
          onConfirm: () => {
            const learnMoreUrl = utils.getTranslatedHelpUrl()
            window.open(learnMoreUrl, '_blank')
          },
          onDismiss: (closeBanner) => {
            setPreference('sourcebox.alert-banner.dismissed', true)
            closeBanner()
          },
          open,
          setOpen,
        }
      : null

  return (
    <Router basename={base}>
      <Root analytics {...footerProp} {...fullScreenProp} alertBannerProps={alertBanner}>
        <AppLayout fullWidth backgroundColor={colors.gray02}>
          <HelperMode />
          <App setShowFooter={setShowFooter} setIsSourceBox={setIsSourceBox} />
        </AppLayout>
      </Root>
    </Router>
  )
}

// Do not make changes to FrontierRoot, all changes should be made to FrontierWrapper
const FrontierRoot = () => {
  return (
    <React.StrictMode>
      <Suspense fallback={<EmptyLoading />}>
        <FrontierBaseProviders
          flagOptions={{ LoadingFallback: EmptyLoading }}
          mockActivities={mockActivities}
          mockFlags={mockFlags}
        >
          <FrontierWrapper />
        </FrontierBaseProviders>
      </Suspense>
    </React.StrictMode>
  )
}

ReactDOM.render(<FrontierRoot />, document.getElementById('root'))

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister()
